/*
 * ActiveCampaignItem - Modern UI Component
 * 
 * Applied Modern Styling Features (Matching DataItem):
 * ✅ Modern card layout with clean header/content/footer structure
 * ✅ Responsive design with showCompactMode for different screen sizes
 * ✅ Consistent modern color palette and theming
 * ✅ Enhanced typography with better font weights and spacing
 * ✅ Contemporary border radius (16px standard)
 * ✅ Improved spacing system (8px, 12px, 16px grid)
 * ✅ Modern popover menu with campaign stats and actions
 * ✅ Clean status badges with semantic color coding
 * ✅ Better accessibility with improved contrast ratios
 * ✅ Consistent design tokens matching DataItem patterns
 * ✅ Responsive action buttons with proper feedback
 * ✅ Modern avatar handling and user display
 * ✅ Campaign-specific stats and metrics display
 * ✅ Proper animation delays and fade-in effects
 */

import React, { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { Image, Pressable, RefreshControl, Text, Platform, Dimensions, Animated, FlatList, ActivityIndicator, StyleSheet, useWindowDimensions } from 'react-native';
import { Swipeable } from 'react-native-gesture-handler';
import styles from '@stylesheet'; // Assuming this is a static stylesheet
import moment from 'moment';
import parseByDelimiter from '@utils/parseByDelimiter';
import { Entypo, Ionicons, AntDesign } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import getDaysAgo from '@utils/getDaysAgo';
import { Title } from '@constants/Themed';
import { CountUp } from 'use-count-up';
import InfoBox from '@components/Global/InfoBox';
import PieChartExample from '@components/dataTables/Charts/PieChart'; // Your ApexChart component
import Button from '@components/UI/Button/Button';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { SmartSaaSIcon } from '@assets/--native';
import { FontAwesome, MaterialIcons, FontAwesome6 } from '@expo/vector-icons';
import DropDownView from '@components/Global/DropDownView';
import UserListItem from '../../UsersScreen/Components/UserListItem';
import { SHOW_MESSAGE_MODAL } from '@reduxLocal/actions/types';
import apis from '@api/api';
import toTitleCase from '@hooks/toTitleCase';
import { useDispatch, useSelector } from 'react-redux';
import { H5, H4, SizableText, View, XStack, YStack, Card, Popover, Separator } from 'tamagui';
import { useDeviceType } from '@hooks/useDeviceType';
import PercentageBar from '@components/Global/PercentageBar';
import { DefaultRootState } from '@reduxLocal/persistState';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import * as Animatable from 'react-native-animatable';

// Component interfaces
interface ActiveCampaignItemProps {
  item: any;
  index?: number;
  handleShareState?: (item: any) => void;
  handleGenLink?: (item: any) => void;
  handleQuickAdd?: (item: any) => void;
  handleSearch?: (item: any) => void;
  compactMode?: boolean;
  listHorizontal?: boolean;
  handleRefresh?: () => void;
  handleShareModal?: (item: any) => void;
  handleActionModal?: (item: any) => void;
  handleDeleteModal?: (item: any) => void;
  handleSettingsModal?: (item: any) => void;
  availableWidth?: number;
}

// Memoize helper functions if they are used frequently in rendering and don't depend on component state
// These are outside the component to prevent re-creation on every render.
const toCapitalCase = (name: string) => {
  if (!name) return '';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

const getInteractions = (item) => {
  let count = 0;
  if (item?.tracker && typeof item.tracker === 'object') {
    const trackerKeys = Object.keys(item.tracker);
    if (Array.isArray(trackerKeys)) {
      count = trackerKeys.reduce((acc, key) => {
        if (item?.tracker[key]?.clicked) {
          acc += 1;
        }
        return acc;
      }, 0);
    }
  }
  return count;
};

// Define scopes with modern color palette and enhanced design
const scopes = [
  { title: 'default', bgColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", icon: <SmartSaaSIcon fill={"white"} style={{ flex: 1, maxWidth: 40 }} height={24} width={24} />, callback: () => { } },
  { title: 'messenger', bgColor: "linear-gradient(135deg, #00D4FF 0%, #0099CC 100%)", icon: <FontAwesome6 name="facebook-messenger" size={20} style={{ flex: 1 }} color="white" />, callback: () => ({ type: SHOW_MESSAGE_MODAL }) },
  { title: 'whatsapp', bgColor: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)", icon: <FontAwesome6 name="whatsapp" size={20} style={{ flex: 1 }} color="white" />, callback: () => { } },
  { title: 'linkedIn', bgColor: "linear-gradient(135deg, #0077B5 0%, #005885 100%)", icon: <FontAwesome6 name="linkedin" size={20} style={{ flex: 1 }} color="white" />, callback: () => { } },
  { title: 'email', bgColor: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", icon: <MaterialIcons name="email" size={20} style={{ flex: 1 }} color="white" />, callback: () => { } },
  { title: 'sms', bgColor: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)", icon: <FontAwesome name="envelope" size={20} style={{ flex: 1 }} color="white" />, callback: () => { } },
  { title: 'twitter', bgColor: "linear-gradient(135deg, #1DA1F2 0%, #0d8bd9 100%)", icon: <FontAwesome6 name="twitter" size={20} style={{ flex: 1 }} color="white" />, callback: () => { } },
  { title: 'instagram', bgColor: "linear-gradient(135deg, #E4405F 0%, #833AB4 50%, #F77737 100%)", icon: <FontAwesome6 name="instagram" size={20} style={{ flex: 1 }} color="white" />, callback: () => { } },
];


const ActiveCampaignItem: React.FC<ActiveCampaignItemProps> = React.memo(({
  item,
  index,
  handleShareState,
  handleGenLink,
  handleQuickAdd,
  handleSearch,
  listHorizontal,
  handleRefresh,
  compactMode,
  handleShareModal,
  handleActionModal,
  handleDeleteModal,
  handleSettingsModal,
  availableWidth
}) => {
  // Hooks and state
  const user = useSelector((state: DefaultRootState) => state.promiseData.data.userData);
  const navigation = useNavigation<any>();
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { width } = useWindowDimensions();
  const { isTablet } = useDeviceType();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Responsive variables
  const isSmallScreen = width < 600;
  const isMediumScreen = width >= 600 && width < 1024;
  const showCompactMode = isSmallScreen || (availableWidth && availableWidth < 350) || compactMode;

  // Use useMemo for derived values that are expensive to calculate and depend on `item`
  // Validate dates before using moment
  const startDate = useMemo(() => {
    if (!item?.start_date) return moment();
    const date = moment(item.start_date);
    return date.isValid() ? date : moment();
  }, [item?.start_date]);

  const endDate = useMemo(() => {
    if (!item?.end_date) return moment().add(30, 'days');
    const date = moment(item.end_date);
    return date.isValid() ? date : moment().add(30, 'days');
  }, [item?.end_date]);

  const durationInDays = useMemo(() => {
    if (!startDate.isValid() || !endDate.isValid()) return 0;
    return endDate.diff(startDate, 'days', false);
  }, [startDate, endDate]);

  const calcRemainingDays = useCallback(() => {
    const today = moment();
    const remainingDays = endDate.diff(today, 'days');
    return remainingDays > 0 ? remainingDays : 0;
  }, [endDate]);

  // Utility functions
  const isOwner = useCallback(() => {
    return item.createdBy?._id === user.id || item.owner === user.id;
  }, [item.createdBy?._id, item.owner, user.id]);

  const hasPermission = useCallback(() => {
    return item.permission || isOwner();
  }, [item.permission, isOwner]);

  // Track interaction function
  const trackInteraction = useCallback(async (action: string) => {
    try {
      // Use a generic tracking method if campaign-specific tracking doesn't exist
      console.log('Campaign interaction:', action, item._id);
    } catch (error) {
      console.log('Failed to track interaction:', error);
    }
  }, [item._id]);

  // Event handlers
  const _handleStartCampaign = useCallback(async () => {
    trackInteraction('start_campaign');
    await apis.startCampaign(item._id);
    handleRefresh?.();
  }, [item._id, handleRefresh, trackInteraction]);

  const _handlePauseCampaign = useCallback(async () => {
    trackInteraction('pause_campaign');
    // Use the same start campaign API for now, or implement pause logic
    await apis.startCampaign(item._id);
    handleRefresh?.();
  }, [item._id, handleRefresh, trackInteraction]);

  const handleNavigateToSalesFunnel = useCallback(() => {
    trackInteraction('edit_campaign');
    navigation.navigate('SalesFunnel', { campaignId: item?._id });
  }, [navigation, item._id, trackInteraction]);

  const handleNavigateToView = useCallback(() => {
    trackInteraction('view_campaign');
    navigation.navigate('SalesFunnel', { campaignId: item?._id });
  }, [navigation, item._id, trackInteraction]);

  // Memoize chartData for PieChartExample to prevent unnecessary re-renders of PieChartExample
  const pieChartData = useMemo(() => {
    if (!item.tracker) return []; // Return empty if no tracker data
    return [
      { name: 'Seen', population: Object.keys(item.tracker).length || 0, limit: item.audienceCount || 100, color: 'white' },
      { name: 'Clicks', population: getInteractions(item) || 0, limit: item.audienceCount || 100, color: 'rgb(255, 99, 132)' },
      { name: 'Bounced', limit: item.audienceCount || 100, population: item?.bounce_list?.length || 0, color: 'red' },
    ];
  }, [item.tracker, item.audienceCount, item.bounce_list]);

  // Status color mapping
  const getStatusColor = useCallback(() => {
    switch (item.status) {
      case 'active':
        return theme ? '#10B981' : '#059669';
      case 'paused':
        return theme ? '#F59E0B' : '#D97706';
      case 'completed':
        return theme ? '#3B82F6' : '#1D4ED8';
      default:
        return theme ? '#6B7280' : '#4B5563';
    }
  }, [item.status, theme]);

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={600}
      delay={index * 100}
      style={[
        styles.modernCard,
        {
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          margin: showCompactMode ? 8 : 12,
          minHeight: showCompactMode ? 320 : 380,
          maxWidth: listHorizontal ? 400 : undefined,
          width: listHorizontal ? 400 : undefined,
        }
      ]}
    >
      {/* Status Badge */}
      <View style={{
        position: 'absolute',
        top: 16,
        right: 16,
        backgroundColor: getStatusColor(),
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
        zIndex: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
        elevation: 4,
      }}>
        <SizableText
          fontSize={11}
          color="white"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing={0.5}
        >
          {toCapitalCase(item.status) || "DRAFT"}
        </SizableText>
      </View>

      {/* Header Section */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        padding: showCompactMode ? 12 : 16,
        borderBottomWidth: 1,
        borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
      }}>
        {/* Avatar */}
        <AvatarHandler
          avatar={item.createdBy?.profile_information?.avatar?.img}
          avatarImageStyle={{
            width: showCompactMode ? 36 : 44,
            height: showCompactMode ? 36 : 44,
            borderRadius: showCompactMode ? 18 : 22,
            borderWidth: 2,
            borderColor: theme ? '#e2e8f0' : '#334155',
          }}
          replacementImageStyle={{
            width: showCompactMode ? 36 : 44,
            height: showCompactMode ? 36 : 44,
            borderRadius: showCompactMode ? 18 : 22,
          }}
        />

        {/* Title and Metadata */}
        <View style={{ flex: 1, marginLeft: 12, marginRight: 60 }}>
          <XStack alignItems="center" gap={8}>
            <H4
              numberOfLines={1}
              color={theme ? "#1e293b" : "#f1f5f9"}
              fontSize={showCompactMode ? 16 : 18}
              fontWeight="600"
              flex={1}
            >
              {parseByDelimiter(item?.campaign_title || "Untitled Campaign")}
            </H4>
          </XStack>

          <XStack alignItems="center" gap={8} marginTop={4}>
            <SizableText
              fontSize={12}
              color={theme ? "#64748b" : "#94a3b8"}
            >
              {getDaysAgo(item.updatedAt)}
            </SizableText>
            <View style={{
              width: 4,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme ? "#64748b" : "#94a3b8",
            }} />
            <SizableText
              fontSize={12}
              color={theme ? "#64748b" : "#94a3b8"}
            >
              {calcRemainingDays()} days left
            </SizableText>
            {item.audienceCount > 0 && (
              <>
                <View style={{
                  width: 4,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: theme ? "#64748b" : "#94a3b8",
                }} />
                <SizableText
                  fontSize={12}
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  {item.audienceCount} audience
                </SizableText>
              </>
            )}
          </XStack>
        </View>

        {/* Actions Menu */}
        <XStack alignItems="center" gap={8} position="absolute" right={16} top={50}>
          {/* Favorite Button */}
          <Pressable
            onPress={() => {/* Handle favorite */}}
            style={{
              padding: 8,
              borderRadius: 20,
              backgroundColor: theme ? '#f8fafc' : '#1e293b',
            }}
          >
            <FontAwesome
              name="heart-o"
              size={16}
              color={theme ? "#64748b" : "#94a3b8"}
            />
          </Pressable>

          {/* Ellipsis Menu */}
          <Popover
            open={isMenuOpen}
            onOpenChange={setIsMenuOpen}
            placement="bottom-end"
          >
            <Popover.Trigger asChild>
              <Pressable
                style={{
                  padding: 8,
                  borderRadius: 20,
                  backgroundColor: theme ? '#f8fafc' : '#1e293b',
                }}
              >
                <MaterialIcons
                  name="more-vert"
                  size={16}
                  color={theme ? "#64748b" : "#94a3b8"}
                />
              </Pressable>
            </Popover.Trigger>

            <Popover.Content
              borderWidth={1}
              borderColor={theme ? "$borderColor" : "$borderColorDark"}
              backgroundColor={theme ? "$background" : "$backgroundDark"}
              padding={0}
              minWidth={180}
              borderRadius={12}
              elevation={5}
            >
              <YStack>
                {/* Campaign Stats */}
                <View style={{
                  padding: 12,
                  alignItems: 'center',
                  borderBottomWidth: 1,
                  borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                }}>
                  <SizableText fontSize={18} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                    ${item.sales_target || 0}
                  </SizableText>
                  <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                    Target
                  </SizableText>
                </View>

                {/* Performance Stats */}
                <View style={{
                  flexDirection: 'row',
                  borderBottomWidth: 1,
                  borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                }}>
                  <View style={{
                    flex: 1,
                    padding: 12,
                    alignItems: 'center',
                    borderRightWidth: 1,
                    borderRightColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                  }}>
                    <SizableText fontSize={16} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                      {Object.keys(item.tracker || {}).length}
                    </SizableText>
                    <SizableText fontSize={10} color={theme ? "#64748b" : "#94a3b8"}>
                      Opens
                    </SizableText>
                  </View>
                  <View style={{
                    flex: 1,
                    padding: 12,
                    alignItems: 'center',
                  }}>
                    <SizableText fontSize={16} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                      {getInteractions(item)}
                    </SizableText>
                    <SizableText fontSize={10} color={theme ? "#64748b" : "#94a3b8"}>
                      Clicks
                    </SizableText>
                  </View>
                </View>

                {/* Action Items */}
                {hasPermission() && (
                  <>
                    <Pressable
                      onPress={() => {
                        setIsMenuOpen(false);
                        handleShareState?.(item);
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        gap: 12,
                      }}
                    >
                      <MaterialIcons
                        name="share"
                        size={18}
                        color={theme ? "#0ea5e9" : "#38bdf8"}
                      />
                      <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                        Share Campaign
                      </SizableText>
                    </Pressable>

                    <Pressable
                      onPress={() => {
                        setIsMenuOpen(false);
                        handleGenLink?.(item);
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        gap: 12,
                      }}
                    >
                      <MaterialIcons
                        name="link"
                        size={18}
                        color={theme ? "#f59e0b" : "#fbbf24"}
                      />
                      <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                        Generate Link
                      </SizableText>
                    </Pressable>

                    <View style={{
                      height: 1,
                      backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                      marginHorizontal: 12,
                    }} />
                  </>
                )}

                {/* Settings */}
                {hasPermission() && (
                  <Pressable
                    onPress={() => {
                      setIsMenuOpen(false);
                      handleSettingsModal?.(item);
                    }}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: 12,
                      gap: 12,
                    }}
                  >
                    <MaterialIcons
                      name="settings"
                      size={18}
                      color={theme ? "#7c3aed" : "#a78bfa"}
                    />
                    <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                      Settings
                    </SizableText>
                  </Pressable>
                )}

                {/* Delete */}
                {hasPermission() && (
                  <>
                    <View style={{
                      height: 1,
                      backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                      marginHorizontal: 12,
                    }} />

                    <Pressable
                      onPress={() => {
                        setIsMenuOpen(false);
                        handleDeleteModal?.(item);
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        gap: 12,
                      }}
                    >
                      <MaterialIcons
                        name="delete"
                        size={18}
                        color="#ef4444"
                      />
                      <SizableText fontSize={14} color="#ef4444">
                        Delete Campaign
                      </SizableText>
                    </Pressable>
                  </>
                )}
              </YStack>
            </Popover.Content>
          </Popover>
        </XStack>
      </View>

      {/* Content Section */}
      <View style={{ flex: 1, padding: showCompactMode ? 12 : 16 }}>
        {/* Campaign Type and Scope */}
        {item.marketing_scope && (
          <XStack gap={8} marginBottom={12}>
            <View style={{
              backgroundColor: theme ? '#e2e8f0' : '#334155',
              padding: 8,
              borderRadius: 8,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
            }}>
              <MaterialIcons
                name="campaign"
                size={16}
                color={theme ? "#475569" : "#cbd5e1"}
              />
              <SizableText
                fontSize={12}
                color={theme ? "#475569" : "#cbd5e1"}
                fontWeight="500"
              >
                {toTitleCase(item.marketing_scope)}
              </SizableText>
            </View>
          </XStack>
        )}

        {/* Campaign Description */}
        {item.description && (
          <View style={{ marginBottom: 16 }}>
            <SizableText
              fontSize={14}
              color={theme ? "#475569" : "#94a3b8"}
              numberOfLines={showCompactMode ? 2 : 3}
            >
              {item.description}
            </SizableText>
          </View>
        )}

        {/* Stats Row */}
        <XStack justifyContent="space-between" alignItems="center" marginBottom={16}>
          <XStack gap={16}>
            <View style={{ alignItems: 'center' }}>
              <MaterialIcons
                name="schedule"
                size={20}
                color={theme ? "#3b82f6" : "#60a5fa"}
              />
              <SizableText fontSize={11} color={theme ? "#64748b" : "#94a3b8"}>
                {calcRemainingDays()}d
              </SizableText>
            </View>

            <View style={{ alignItems: 'center' }}>
              <MaterialIcons
                name="people"
                size={20}
                color={theme ? "#059669" : "#10b981"}
              />
              <SizableText fontSize={11} color={theme ? "#64748b" : "#94a3b8"}>
                {item.audienceCount || 0}
              </SizableText>
            </View>

            <View style={{ alignItems: 'center' }}>
              <MaterialIcons
                name="visibility"
                size={20}
                color={theme ? "#7c3aed" : "#a78bfa"}
              />
              <SizableText fontSize={11} color={theme ? "#64748b" : "#94a3b8"}>
                {Object.keys(item.tracker || {}).length}
              </SizableText>
            </View>

            <View style={{ alignItems: 'center' }}>
              <MaterialIcons
                name="touch-app"
                size={20}
                color={theme ? "#f59e0b" : "#fbbf24"}
              />
              <SizableText fontSize={11} color={theme ? "#64748b" : "#94a3b8"}>
                {getInteractions(item)}
              </SizableText>
            </View>
          </XStack>

          {/* Owner Indicator */}
          {!isOwner() && (
            <View style={{
              backgroundColor: '#10b981',
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 12,
            }}>
              <SizableText fontSize={10} color="white" fontWeight="600">
                Shared
              </SizableText>
            </View>
          )}
        </XStack>

        {/* Campaign Scope Icons */}
        {item.scopes && item.scopes.length > 0 && (
          <XStack gap={6} flexWrap="wrap" marginBottom={16}>
            {scopes.slice(0, 4).map((scope, idx) => 
              item.scopes.includes(scope.title) && (
                <View
                  key={idx}
                  style={{
                    backgroundColor: theme ? '#f1f5f9' : '#334155',
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 8,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {React.cloneElement(scope.icon, { size: 12 })}
                  <SizableText fontSize={11} color={theme ? "#475569" : "#cbd5e1"}>
                    {scope.title}
                  </SizableText>
                </View>
              )
            )}
          </XStack>
        )}
      </View>

      {/* Action Buttons */}
      <View style={{
        flexDirection: showCompactMode ? 'column' : 'row',
        padding: showCompactMode ? 12 : 16,
        gap: showCompactMode ? 8 : 12,
        borderTopWidth: 1,
        borderTopColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
      }}>
        {/* Quick Actions */}
        <XStack gap={8} flex={showCompactMode ? undefined : 1}>
          <Pressable
            onPress={item.status === 'active' ? _handlePauseCampaign : _handleStartCampaign}
            disabled={!hasPermission()}
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
              paddingHorizontal: 13,
              borderRadius: 8,
              backgroundColor: item.status === 'active' 
                ? (theme ? '#fef2f2' : '#7f1d1d')
                : (theme ? '#f0fdf4' : '#064e3b'),
              opacity: hasPermission() ? 1 : 0.5,
            }}
          >
            <MaterialIcons
              name={item.status === 'active' ? 'pause' : 'play-arrow'}
              size={16}
              color={item.status === 'active' 
                ? (theme ? '#dc2626' : '#f87171')
                : (theme ? '#059669' : '#10b981')
              }
            />
            <SizableText
              fontSize={12}
              paddingHorizontal={10}
              color={item.status === 'active' 
                ? (theme ? '#dc2626' : '#f87171')
                : (theme ? '#059669' : '#10b981')
              }
              marginLeft={4}
            >
              {item.status === 'active' ? 'Pause' : 'Start'}
            </SizableText>
          </Pressable>

          <Pressable
            onPress={handleNavigateToSalesFunnel}
            disabled={!hasPermission()}
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
              borderRadius: 8,
              backgroundColor: hasPermission()
                ? (theme ? '#fef3c7' : '#78350f')
                : (theme ? '#f1f5f9' : '#1e293b'),
              opacity: hasPermission() ? 1 : 0.5,
            }}
          >
            <MaterialIcons
              name="edit"
              size={16}
              color={hasPermission() ? (theme ? '#d97706' : '#fbbf24') : (theme ? '#94a3b8' : '#64748b')}
            />
            <SizableText
              fontSize={12}
              color={hasPermission() ? (theme ? '#d97706' : '#fbbf24') : (theme ? '#94a3b8' : '#64748b')}
              marginLeft={4}
            >
              Edit
            </SizableText>
          </Pressable>
        </XStack>

        {/* Primary Action */}
        <Button
          onPress={handleNavigateToView}
          customStyle={{
            flex: showCompactMode ? undefined : 2,
            minHeight: 40,
            borderRadius: 8,
            backgroundColor: theme ? '#3b82f6' : '#1d4ed8',
          }}
          buttonText="View Campaign"
        />
      </View>
    </Animatable.View>
  );
});

// Export with default name for compatibility
export default React.memo(ActiveCampaignItem);